import React, { useState } from "react";
import { Instagram, Facebook, Mail, MapPin, Send, Phone } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TikTokIcon = () => (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
        <path d="M19.59 6.69a4.83 4.83 0 01-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 01-2.88 2.5 2.89 2.89 0 01-2.89-2.89 2.89 2.89 0 012.89-2.89c.28 0 .54.04.79.1V9.01a6.33 6.33 0 00-.79-.05 6.34 6.34 0 00-6.34 6.34 6.34 6.34 0 006.34 6.34 6.34 6.34 0 006.33-6.34V8.94a8.17 8.17 0 004.77 1.53V7.02a4.85 4.85 0 01-1-.33z"/>
    </svg>
);

const Contact = () => {
    const [form, setForm] = useState({ name: "", email: "", phone: "", message: "", website: "" });
    const [loading, setLoading] = useState(false);

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!form.name.trim() || !form.email.trim() || !form.message.trim()) {
            toast.error("Please fill in name, email, and message.");
            return;
        }
        setLoading(true);
        try {
            const res = await axios.post(`${API}/contact`, form);
            toast.success(res.data.message || "Message sent!");
            setForm({ name: "", email: "", phone: "", message: "", website: "" });
        } catch (e) {
            toast.error(e.response?.status === 429 ? "Too many messages — please wait a few minutes and try again." : "Something went wrong. Please try again.");
        } finally {
            setLoading(false);
        }
    };

    const update = (field) => (e) => setForm(prev => ({ ...prev, [field]: e.target.value }));

    const inputCls = "w-full h-12 px-4 bg-white border-2 border-[#3D2008] font-manrope text-[#3D2008] placeholder-[#5C4A35]/50 focus:outline-none focus:border-[#C4522A] focus:shadow-[4px_4px_0px_0px_rgba(196,82,42,0.2)] transition-shadow";

    return (
        <div className="pt-16">
            {/* Hero */}
            <section className="py-20 px-4 bg-[#3D2008]" data-testid="contact-hero">
                <div className="max-w-7xl mx-auto">
                    <span className="font-marker text-[#C9A84C] text-2xl -rotate-1 inline-block mb-4">Say Hello</span>
                    <h1 className="font-anton text-5xl sm:text-7xl lg:text-8xl text-[#F5EDD6] uppercase leading-none mb-4">Contact Us</h1>
                    <p className="font-manrope text-[#F5EDD6]/70 text-xl max-w-2xl">
                        Questions, ideas, sponsorship inquiries, or just want to say hi — we'd love to hear from you.
                    </p>
                </div>
            </section>

            {/* Contact grid */}
            <section className="py-20 px-4 bg-[#F5EDD6]" data-testid="contact-section">
                <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-16">
                    {/* Form */}
                    <div>
                        <h2 className="font-anton text-3xl text-[#3D2008] uppercase mb-8">Send a Message</h2>
                        <form onSubmit={handleSubmit} data-testid="contact-form" className="space-y-4">
                            <input type="text" name="website" value={form.website} onChange={update("website")} tabIndex={-1} autoComplete="off" aria-hidden="true" className="hidden" />
                            <div>
                                <label className="font-manrope text-sm font-semibold text-[#3D2008] uppercase tracking-wide block mb-1">Name *</label>
                                <input
                                    type="text"
                                    placeholder="Your full name"
                                    value={form.name}
                                    onChange={update("name")}
                                    required
                                    data-testid="contact-name-input"
                                    className={inputCls}
                                />
                            </div>
                            <div>
                                <label className="font-manrope text-sm font-semibold text-[#3D2008] uppercase tracking-wide block mb-1">Email *</label>
                                <input
                                    type="email"
                                    placeholder="your@email.com"
                                    value={form.email}
                                    onChange={update("email")}
                                    required
                                    data-testid="contact-email-input"
                                    className={inputCls}
                                />
                            </div>
                            <div>
                                <label className="font-manrope text-sm font-semibold text-[#3D2008] uppercase tracking-wide block mb-1">Phone (optional)</label>
                                <input
                                    type="tel"
                                    placeholder="(406) 555-0000"
                                    value={form.phone}
                                    onChange={update("phone")}
                                    data-testid="contact-phone-input"
                                    className={inputCls}
                                />
                            </div>
                            <div>
                                <label className="font-manrope text-sm font-semibold text-[#3D2008] uppercase tracking-wide block mb-1">Message *</label>
                                <textarea
                                    placeholder="Tell us what's on your mind..."
                                    value={form.message}
                                    onChange={update("message")}
                                    required
                                    rows={6}
                                    data-testid="contact-message-input"
                                    className="w-full px-4 py-3 bg-white border-2 border-[#3D2008] font-manrope text-[#3D2008] placeholder-[#5C4A35]/50 focus:outline-none focus:border-[#C4522A] focus:shadow-[4px_4px_0px_0px_rgba(196,82,42,0.2)] transition-shadow resize-none"
                                />
                            </div>
                            <button
                                type="submit"
                                disabled={loading}
                                data-testid="contact-submit-btn"
                                className="w-full py-4 bg-[#C4522A] text-white font-anton uppercase tracking-wider text-lg border-2 border-[#3D2008] shadow-[5px_5px_0px_0px_rgba(61,32,8,0.3)] hover:translate-x-[2px] hover:translate-y-[2px] hover:shadow-[2px_2px_0px_0px_rgba(61,32,8,0.3)] transition-transform duration-150 disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                            >
                                <Send size={18} />
                                {loading ? "Sending..." : "Send Message"}
                            </button>
                        </form>
                    </div>

                    {/* Info */}
                    <div className="space-y-8">
                        <div>
                            <h2 className="font-anton text-3xl text-[#3D2008] uppercase mb-6">Find Us</h2>
                            <div className="space-y-4">
                                <div className="flex items-start gap-4 p-4 bg-white border-2 border-[#3D2008] shadow-[4px_4px_0px_0px_rgba(61,32,8,0.1)]">
                                    <Phone size={20} className="text-[#C4522A] mt-0.5 flex-shrink-0" />
                                    <div>
                                        <p className="font-manrope font-semibold text-[#3D2008]">Phone</p>
                                        <a href="tel:+14064597079" className="font-manrope text-sm text-[#5C4A35] hover:text-[#C4522A] transition-colors">406-459-7079</a>
                                    </div>
                                </div>
                                <div className="flex items-start gap-4 p-4 bg-white border-2 border-[#3D2008] shadow-[4px_4px_0px_0px_rgba(61,32,8,0.1)]">
                                    <Mail size={20} className="text-[#C4522A] mt-0.5 flex-shrink-0" />
                                    <div>
                                        <p className="font-manrope font-semibold text-[#3D2008]">Email</p>
                                        <a href="mailto:info@seeleyskatepark.org" className="font-manrope text-sm text-[#5C4A35] hover:text-[#C4522A] transition-colors">info@seeleyskatepark.org</a>
                                    </div>
                                </div>
                                <div className="flex items-start gap-4 p-4 bg-white border-2 border-[#3D2008] shadow-[4px_4px_0px_0px_rgba(61,32,8,0.1)]">
                                <MapPin size={20} className="text-[#C4522A] mt-0.5 flex-shrink-0" />
                                    <div>
                                        <p className="font-manrope font-semibold text-[#3D2008]">Location</p>
                                        <p className="font-manrope text-sm text-[#5C4A35]">Seeley Lake, Montana</p>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div>
                            <h3 className="font-anton text-2xl text-[#3D2008] uppercase mb-4">Follow Us</h3>
                            <div className="space-y-3">
                                {[
                    { icon: <Instagram size={20} />, label: "Instagram", handle: "@seeleylakeskatepark", href: "https://www.instagram.com/seeleylakeskatepark/", testId: "contact-instagram-link", color: "hover:bg-[#E1306C]" },
                                    { icon: <Facebook size={20} />, label: "Facebook", handle: "Seeley Lake Skatepark Association", href: "https://www.facebook.com/people/Seeley-Lake-Skatepark-Association/61589983814332/", testId: "contact-facebook-link", color: "hover:bg-[#4A7FA5]" },
                                ].map(s => (
                                    <a key={s.label} href={s.href} target="_blank" rel="noopener noreferrer"
                                        data-testid={s.testId}
                                        className={`flex items-center gap-4 p-4 bg-white border-2 border-[#3D2008] shadow-[4px_4px_0px_0px_rgba(61,32,8,0.1)] ${s.color} hover:text-white hover:border-transparent transition-colors duration-200 group`}>
                                        <span className="text-[#C4522A] group-hover:text-white transition-colors">{s.icon}</span>
                                        <div>
                                            <p className="font-manrope font-semibold text-[#3D2008] group-hover:text-white transition-colors">{s.label}</p>
                                            <p className="font-manrope text-xs text-[#5C4A35] group-hover:text-white/80 transition-colors">{s.handle}</p>
                                        </div>
                                    </a>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>
            </section>
        </div>
    );
};

export default Contact;
